import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useAdBuilderDataStore from "../../store/useAdBuilderDataStore";
import Spinner from "@/components/ui/Spinner";
import useGlobalStore from "@/store/globalStore";
import useCanvasCommentsStore from "../../store/useCanvasCommentsStore";
import AdBuilderHeader from "./ad-builder-header";
import AdBuilderVersionChips from "./ad-builder-version-chips";
import AdBuilderStatusBar from "./ad-builder-status-bar";
import SharePreviewButton from "./share-preview-button";
import AdBuilderCanvas from "./ad-builder-canvas";
import AdBuilderForm from "./ad-builder-form";

interface AdBuilderProps {
  onSave?: (data: AdBuilderData) => void;
}

interface AdBuilderData {
  adTitle: string;
  primaryText: string;
  headline: string;
  description: string;
  linkAcrossVersions: {
    primaryText: boolean;
    headline: boolean;
    description: boolean;
  };
}

// Local media override — keyed by canvas_component_info_id
interface MediaOverride {
  url: string; // object URL for preview
  file: File;
  /** Slot this upload belongs to, for multi-slot components. */
  slotIndex?: number;
}

const AdBuilder: React.FC<AdBuilderProps> = ({ onSave }) => {
  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const adTypeId = searchParams.get("adTypeId");

  const {
    adBuilderData,
    isLoading,
    isSaving,
    isTransitioning,
    fetchAdBuilderData,
    assetVersions,
    activeVersionId,
    fetchAssetVersions,
    setActiveVersionId,
    updateAssetVersion,
    transitionStatus,
    createAssetVersion,
    deleteAssetVersion,
    downloadAssetVersionMedia,
    changelog,
    isChangelogLoading,
    fetchChangelog,
  } = useAdBuilderDataStore();
  const [isDownloadingMedia, setIsDownloadingMedia] = useState(false);
  const [versionToDelete, setVersionToDelete] = useState<{
    id: string;
    title: string;
  } | null>(null);

  const assetsRowId = searchParams.get("assetsRowId");
  const versionIdFromUrl = searchParams.get("versionId");

  // Refetched when the active version changes: restricted parameter values are
  // stored per version, so switching versions must reload their saved answers.
  useEffect(() => {
    if (campaignId && adTypeId) {
      fetchAdBuilderData(campaignId, adTypeId, assetsRowId, activeVersionId);
    }
  }, [campaignId, adTypeId, assetsRowId, activeVersionId]);

  useEffect(() => {
    if (assetsRowId) {
      fetchAssetVersions(assetsRowId);
    }
  }, [assetsRowId]);

  useEffect(() => {
    if (activeVersionId) {
      fetchComments(activeVersionId);
      fetchChangelog(activeVersionId);
    }
  }, [activeVersionId]);

  // Reset comments when unmounting
  useEffect(() => {
    return () => resetComments();
  }, []);

  // Once versions load, activate the version from URL or default to first
  useEffect(() => {
    if (assetVersions.length === 0) return;
    const target = versionIdFromUrl
      ? assetVersions.find((v) => v.id === versionIdFromUrl)
      : null;
    const resolvedId = target ? target.id : assetVersions[0].id;
    setActiveVersionId(resolvedId);
    // Sync URL if no versionId present yet
    if (!versionIdFromUrl) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("versionId", resolvedId);
          return next;
        },
        { replace: true },
      );
    }
  }, [assetVersions]);

  const { showError } = useGlobalStore();
  const {
    comments,
    isDrawerOpen,
    activeCommentId,
    pendingPin,
    fetchComments,
    openDrawer,
    closeDrawer,
    setPendingPin,
    reset: resetComments,
  } = useCanvasCommentsStore();

  const [commentModeActive, setCommentModeActive] = useState(false);

  // Status transitions apply directly from the status bar, so no modal or
  // comment state is needed — only the error a rejected transition returns.
  const [permissionError, setPermissionError] = useState<string | null>(null);
  // Covers the whole transition sequence, not just the POST. The store's
  // `isTransitioning` clears as soon as the request resolves, but the versions
  // and changelog are refetched after that — so relying on it alone re-enabled
  // the button while the bar was still showing stale actions.
  const [isApplyingTransition, setIsApplyingTransition] = useState(false);
  // Same reasoning for saving: the store's `isSaving` clears when the update
  // request resolves, but the handler then refetches versions and may
  // auto-advance the status, so the button must stay busy until all of it ends.
  const [isApplyingSave, setIsApplyingSave] = useState(false);
  // Restricted parameter edits, keyed by `${parameterId}::${fieldKey}` — a
  // parameter can expose several fields, so the id alone is not unique.
  const [restrictedValues, setRestrictedValues] = useState<
    Record<string, string>
  >({});

  // Tracks the interface_id currently rendered on the canvas (set inside the canvas IIFE)
  const activeInterfaceIdRef = useRef<string>("");

  const [adTitle, setAdTitle] = useState("");
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [activeDevice, setActiveDevice] = useState<string>("");

  // Form state — keyed by sub_master name for flexible mapping
  const [contentValues, setContentValues] = useState<Record<string, string>>(
    {},
  );

  // Media overrides — keyed by canvas_component_info_id
  const [mediaOverrides, setMediaOverrides] = useState<
    Record<string, MediaOverride>
  >({});

  // Carousel active slide index — keyed by canvas_component_info_id
  const [carouselSlideIndex, setCarouselSlideIndex] = useState<
    Record<string, number>
  >({});

  // Audio play state — keyed by canvas_component_info_id
  const [audioPlaying, setAudioPlaying] = useState<Record<string, boolean>>({});
  const audioRefs = useRef<Record<string, HTMLAudioElement | null>>({});

  // Audio playlist active track — keyed by canvas_component_info_id → track index
  const [activeTrack, setActiveTrack] = useState<Record<string, number>>({});

  // URL validation errors — keyed by field name
  const [urlErrors, setUrlErrors] = useState<Record<string, string>>({});

  // Keep a ref to assetVersions so we can read latest without triggering effects
  const assetVersionsRef = useRef(assetVersions);
  useEffect(() => {
    assetVersionsRef.current = assetVersions;
  }, [assetVersions]);

  /**
   * Seed the restricted inputs from this version's saved answers.
   *
   * Only `saved_value` is read, never the field's `value`. For a "Multiple"
   * parameter `value` is the list of *allowed* options, so seeding from it would
   * present an arbitrary option as though someone had chosen it. A field with no
   * saved answer stays empty.
   *
   * Replaces state rather than merging, because switching versions must not
   * carry the previous version's selections across.
   */
  useEffect(() => {
    const params = adBuilderData?.restricted_parameters ?? [];

    const seeded: Record<string, string> = {};
    params.forEach((param) => {
      (param.fields ?? []).forEach((field) => {
        const saved = field.saved_value;
        if (saved == null || saved === "") return;

        seeded[`${param.id}::${field.key ?? "value"}`] = Array.isArray(saved)
          ? String(saved[0] ?? "")
          : String(saved);
      });
    });

    setRestrictedValues(seeded);
  }, [adBuilderData]);

  /**
   * Fields the template author locked (Properties → Editable off). Their value
   * is fixed by the template: shown here but not changeable. Keyed by
   * sub-master name, which is how content/objective items are keyed too.
   * A missing `editable` flag means editable, so existing templates are
   * unaffected.
   */
  const lockedFieldNames = useMemo(() => {
    const locked = new Set<string>();
    adBuilderData?.canvas.components.forEach((comp: any) => {
      if (comp.aggregated_json?.properties?.editable === false) {
        locked.add(comp.canvas_component_sub_master_name);
      }
    });
    return locked;
  }, [adBuilderData]);

  /** Canvas component ids whose media the template locked. */
  const lockedMediaComponentIds = useMemo(() => {
    const locked = new Set<string>();
    adBuilderData?.canvas.components.forEach((comp: any) => {
      if (comp.aggregated_json?.properties?.editable === false) {
        locked.add(comp.id);
      }
    });
    return locked;
  }, [adBuilderData]);

  /**
   * Human-readable description of where each media component sits on the
   * canvas, so an upload slot can say which part of the ad it fills rather
   * than just "Card 1". Keyed by canvas_component_info_id.
   */
  const mediaSlotDescriptions = useMemo(() => {
    const byId: Record<string, { label: string; position: string }> = {};
    const components = adBuilderData?.canvas.components ?? [];

    // Work out the canvas height so positions can be described in thirds
    const canvasHeight =
      adBuilderData?.canvas.canvas_master?.canvas_info_aggregated
        ?.canvasHeight ?? 0;

    components.forEach((comp: any) => {
      const master = comp.canvas_component_master_name;
      // `avatar` is included so the profile-picture slot gets a real caption
      // describing where it sits, rather than falling back to generic text.
      if (
        !["image", "video", "audio", "preview", "avatar"].includes(master)
      )
        return;

      // Any interface's location will do — we only need rough placement
      const loc: any = Object.values(comp.locations ?? {})[0];
      const box = loc?.location_json;

      let position = "";
      if (box && canvasHeight > 0) {
        const centreY = box.y + box.height / 2;
        const band =
          centreY < canvasHeight / 3
            ? "top"
            : centreY < (canvasHeight * 2) / 3
              ? "middle"
              : "bottom";
        // Horizontal placement matters for small chrome pieces like the
        // avatar, where "top of the ad" alone is not enough to locate it.
        const canvasWidth =
          adBuilderData?.canvas.canvas_master?.canvas_info_aggregated
            ?.canvasWidth ?? 0;
        let side = "";
        if (canvasWidth > 0) {
          const centreX = box.x + box.width / 2;
          side =
            centreX < canvasWidth / 3
              ? "left"
              : centreX < (canvasWidth * 2) / 3
                ? "centre"
                : "right";
        }
        const place = side && side !== "centre" ? `${band} ${side}` : band;
        position = `${place} of the ad · ${Math.round(box.width)}×${Math.round(box.height)}px`;
      }

      byId[comp.id] = {
        label:
          comp.aggregated_json?.label ??
          comp.canvas_component_sub_master_display_name ??
          "Media",
        position,
      };
    });

    return byId;
  }, [adBuilderData]);

  // Seed content fields from API data, then overlay the active version's saved JSON.
  // Depends only on adBuilderData and activeVersionId — NOT assetVersions —
  // so that refreshing versions for media signed URLs doesn't wipe user-typed text.
  useEffect(() => {
    if (!adBuilderData) return;

    // Content authored on the template (ad-builder) lives on the canvas
    // component as `aggregated_json.content`, keyed by sub-master name. It is
    // the pre-filled default here when this asset has no saved value yet.
    const templateContentByName: Record<string, string> = {};
    adBuilderData.canvas.components.forEach((comp: any) => {
      const value = comp.aggregated_json?.content;
      if (value != null && value !== "") {
        templateContentByName[comp.canvas_component_sub_master_name] = value;
      }
    });
    // Locked fields always show the template's value, even if this asset has a
    // stale saved value from before the template author locked the field.
    adBuilderData.canvas.components.forEach((comp: any) => {
      if (comp.aggregated_json?.properties?.editable === false) {
        const name = comp.canvas_component_sub_master_name;
        const value = comp.aggregated_json?.content;
        if (value != null) templateContentByName[name] = value;
      }
    });

    const initial: Record<string, string> = {};
    const seed = (item: { name: string; saved_value?: string | null }) => {
      // A locked field is fixed by the template, so its value wins outright.
      if (lockedFieldNames.has(item.name)) {
        initial[item.name] = templateContentByName[item.name] ?? "";
        return;
      }
      initial[item.name] =
        item.saved_value ?? templateContentByName[item.name] ?? "";
    };
    adBuilderData.content_objective.content.forEach(seed);
    adBuilderData.content_objective.objective.forEach(seed);

    // Overlay content_json / objective_json from the active version if available
    const activeVersion = assetVersionsRef.current.find(
      (v) => v.id === activeVersionId,
    );
    if (activeVersion) {
      setAdTitle(activeVersion.ad_title ?? "");
      if (activeVersion.content_json) {
        Object.entries(activeVersion.content_json).forEach(([key, val]) => {
          initial[key] = String(val ?? "");
        });
      }
      if (activeVersion.objective_json) {
        Object.entries(activeVersion.objective_json).forEach(([key, val]) => {
          initial[key] = String(val ?? "");
        });
      }
    }

    setContentValues(initial);
  }, [adBuilderData, activeVersionId]);

  // Detect available device types from component locations and set default active device
  useEffect(() => {
    if (!adBuilderData) return;
    const devices = new Set<string>();
    adBuilderData.canvas.components.forEach((comp) => {
      Object.keys(comp.locations).forEach((key) => devices.add(key));
    });
    if (devices.size === 0) {
      showError("No device interfaces found in canvas components.");
      return;
    }
    const defaultDevice = devices.has("mobile")
      ? "mobile"
      : Array.from(devices)[0];
    setActiveDevice(defaultDevice);
  }, [adBuilderData]);

  // Link across versions checkboxes
  const [linkAcrossVersions, setLinkAcrossVersions] = useState({
    primaryText: true,
    headline: true,
    description: false,
  });

  const handleSaveChanges = async () => {
    if (!activeVersionId || isApplyingSave) return;
    setIsApplyingSave(true);

    const contentKeys = new Set(
      adBuilderData?.content_objective.content.map((i) => i.name) ?? [],
    );
    const objectiveKeys = new Set(
      adBuilderData?.content_objective.objective.map((i) => i.name) ?? [],
    );

    const content_json: Record<string, any> = {};
    const objective_json: Record<string, any> = {};
    Object.entries(contentValues).forEach(([key, val]) => {
      if (contentKeys.has(key)) content_json[key] = val;
      else if (objectiveKeys.has(key)) objective_json[key] = val;
    });

    // Keys are already slot-qualified for multi-slot components
    // (`<componentId>_<slotIndex>`), and the backend stores media_json under
    // whatever key it receives — so each carousel card round-trips separately.
    const media_files: Record<string, File> = {};
    Object.entries(mediaOverrides).forEach(([overrideKey, override]) => {
      media_files[overrideKey] = override.file;
    });

    // Restricted parameter values are saved against the version, not the ad
    // category — the category holds the allowed options, so writing a selection
    // back there would overwrite the shared configuration.
    //
    // Only editable modes are sent. "Default" is fixed by the ad category and
    // rendered read-only, so it has nothing to persist. Every editable field is
    // included even when blank, so clearing a selection is expressible: the
    // backend deletes the row for an empty value.
    const restricted_parameters = (
      adBuilderData?.restricted_parameters ?? []
    ).flatMap((param) => {
      if (param.value_type !== "multi_publisher" && param.value_type !== "user_defined") {
        return [];
      }

      return (param.fields ?? []).map((field) => ({
        ad_category_parameter_value_id: param.id,
        parameter_definition_id: param.parameter_definition?.id ?? null,
        parameter_key: param.key ?? param.parameter_definition?.key ?? "",
        field_key: field.key ?? null,
        value: restrictedValues[`${param.id}::${field.key ?? "value"}`] ?? null,
      }));
    });

    try {
      await updateAssetVersion(activeVersionId, {
        ad_title: adTitle || undefined,
        ...(Object.keys(content_json).length > 0 && { content_json }),
        ...(Object.keys(objective_json).length > 0 && { objective_json }),
        ...(Object.keys(media_files).length > 0 && { media_files }),
        ...(restricted_parameters.length > 0 && { restricted_parameters }),
      });

      if (Object.keys(media_files).length > 0) {
        setMediaOverrides((prev) => {
          Object.values(prev).forEach((o) => URL.revokeObjectURL(o.url));
          return {};
        });
      }

      // A briefed asset that someone has actually edited is by definition being
      // worked on, so saving moves it to the next status automatically rather
      // than making the user set it by hand. Only this one hop is automatic:
      // every later transition stays an explicit, reviewed decision.
      //
      // Guarded by `available_transitions` so it cannot bypass the permission
      // rules — a user who may not advance the status simply saves as normal.
      const versionBeforeSave = assetVersions.find(
        (v) => v.id === activeVersionId,
      );
      const canAutoAdvance =
        versionBeforeSave?.status === "briefed" &&
        (versionBeforeSave?.available_transitions ?? []).some(
          (t) => t.status === "in_progress",
        );

      if (canAutoAdvance) {
        try {
          await transitionStatus(activeVersionId, { status: "in_progress" });
          fetchChangelog(activeVersionId);
        } catch {
          // The save itself succeeded, so a failed auto-advance must not be
          // reported as a failed save. The status simply stays as it was and
          // the user can move it manually.
        }
      }

      // Always refresh versions after save to get signed URLs in media_json
      if (assetsRowId) {
        await fetchAssetVersions(assetsRowId);
      }

      onSave?.({
        adTitle,
        primaryText: contentValues["primary_text"] ?? "",
        headline: contentValues["content_title"] ?? "",
        description: contentValues["description"] ?? "",
        linkAcrossVersions,
      });
    } catch (error) {
      showError(
        error instanceof Error && error.message && error.message !== "Failed to save version"
          ? error.message
          : "Failed to save changes. Please try again.",
      );
    } finally {
      setIsApplyingSave(false);
    }
  };

  /**
   * Key a media override.
   *
   * Multi-slot components (carousels, playlists) store one override per slot,
   * so the key carries the slot index. Keying by component id alone made every
   * card in a carousel share a single entry — uploading to one card replaced
   * the image on all of them.
   */
  const overrideKeyFor = (componentInfoId: string, slotIndex?: number) =>
    slotIndex === undefined
      ? componentInfoId
      : `${componentInfoId}_${slotIndex}`;

  // Handle media file selection for a slot
  const handleMediaSelect = (
    componentInfoId: string,
    file: File,
    slotIndex?: number,
  ) => {
    const url = URL.createObjectURL(file);
    setMediaOverrides((prev) => ({
      ...prev,
      [overrideKeyFor(componentInfoId, slotIndex)]: { url, file, slotIndex },
    }));
  };

  // Clear a media override
  const handleMediaClear = (componentInfoId: string, slotIndex?: number) => {
    setMediaOverrides((prev) => {
      const key = overrideKeyFor(componentInfoId, slotIndex);
      const next = { ...prev };
      if (next[key]) {
        URL.revokeObjectURL(next[key].url);
        delete next[key];
      }
      return next;
    });
  };

  // Build a map: sub_master_name → live content value, for canvas text overlay
  // e.g. "content_title" → what the user typed in the headline field
  const contentBySubMaster: Record<string, string> = {};
  if (adBuilderData) {
    adBuilderData.content_objective.content.forEach((item) => {
      const val = contentValues[item.name];
      if (val !== undefined) contentBySubMaster[item.name] = val;
    });
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner />
      </div>
    );
  }

  const campaignName = adBuilderData?.headers.campaign.name ?? "";
  const adTypeName = adBuilderData?.headers.ad_type.name ?? "";
  const adCategoryName = adBuilderData?.headers.ad_category.name ?? "";
  const mediaSlots = adBuilderData?.media.slots ?? [];
  const contentItems = adBuilderData?.content_objective.content ?? [];
  const objectiveItems = adBuilderData?.content_objective.objective ?? [];

  // Determine if the media component is video (used for carousel type detection)
  const isVideoMedia =
    adBuilderData?.canvas.components.some(
      (comp) => comp.canvas_component_master_name === "video",
    ) ?? false;

  console.log(activeVersionId);

  return (
    <div>
      {/* Header */}
      <AdBuilderHeader
        campaignName={campaignName}
        adCategoryName={adCategoryName}
        adTypeName={adTypeName}
        publisherName={adBuilderData?.headers.publisher.name ?? ""}
        onBack={() => navigate(-1)}
      />

      <hr className="border-gray-300 my-5" />

      {/* Version chips */}
      <AdBuilderVersionChips
        assetVersions={assetVersions}
        activeVersionId={activeVersionId}
        versionToDelete={versionToDelete}
        onSelect={(versionId) => {
          setActiveVersionId(versionId);
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.set("versionId", versionId);
              return next;
            },
            { replace: true },
          );
          if (campaignId && adTypeId) {
            fetchAdBuilderData(campaignId, adTypeId, assetsRowId);
          }
        }}
        onCreate={(rowId) => createAssetVersion(rowId)}
        onDeleteRequest={(version) => setVersionToDelete(version)}
        onDeleteConfirm={async (versionId) => {
          await deleteAssetVersion(versionId);
          setVersionToDelete(null);
        }}
        onDeleteCancel={() => setVersionToDelete(null)}
      />

      {/* Status bar / Ad Title */}
      <AdBuilderStatusBar
        activeVersion={assetVersions.find((v) => v.id === activeVersionId)}
        renderShare={(textColor) => (
          <SharePreviewButton
            versionId={activeVersionId}
            status={assetVersions.find((v) => v.id === activeVersionId)?.status ?? "draft"}
            color={textColor}
          />
        )}
        adTitle={adTitle}
        onAdTitleChange={setAdTitle}
        isTransitioning={isTransitioning || isApplyingTransition}
        permissionError={permissionError}
        isDownloading={isDownloadingMedia}
        onDownload={async () => {
          if (!activeVersionId) return;
          try {
            setIsDownloadingMedia(true);
            await downloadAssetVersionMedia(activeVersionId);
          } catch (err: any) {
            showError(err?.message ?? "Failed to download asset media.");
          } finally {
            setIsDownloadingMedia(false);
          }
        }}
        // Applied directly: the status buttons are already explicit, named
        // actions ("Send for Review"), so a confirmation step asking the same
        // question added a click without adding information. No comment is
        // collected, so none is sent.
        onTransitionClick={async (action) => {
          if (!activeVersionId || isApplyingTransition) return;
          try {
            setIsApplyingTransition(true);
            setPermissionError(null);
            await transitionStatus(activeVersionId, {
              status: action.targetStatus,
            });
            // The transition endpoint returns the bare model, without
            // `available_transitions` / `editable` / `is_locked` — those are
            // added only by the list and show endpoints. Merging its response
            // left the previous status's action buttons in place, so the bar
            // showed the new status with stale buttons until a page reload.
            // Refetching pulls the enriched shape.
            if (assetsRowId) {
              await fetchAssetVersions(assetsRowId);
            }
            await fetchChangelog(activeVersionId);
          } catch (error: any) {
            // Check for permission error (403)
            if (error?.response?.status === 403) {
              setPermissionError(
                error?.response?.data?.message ||
                  "You don't have permission to perform this action",
              );
            } else {
              showError("Failed to update status. Please try again.");
            }
          } finally {
            setIsApplyingTransition(false);
          }
        }}
      />

      {/* Main Content */}
      <div className="px-6 py-6">
        <div>
          {/* Left Column - Preview */}
          <AdBuilderCanvas
            adBuilderData={adBuilderData}
            activeDevice={activeDevice}
            setActiveDevice={setActiveDevice}
            showFullScreen={showFullScreen}
            setShowFullScreen={setShowFullScreen}
            commentModeActive={commentModeActive}
            setCommentModeActive={setCommentModeActive}
            comments={comments}
            activeCommentId={activeCommentId}
            pendingPin={pendingPin}
            isDrawerOpen={isDrawerOpen}
            activeVersionId={activeVersionId}
            activeInterfaceIdRef={activeInterfaceIdRef}
            isSaving={isSaving || isApplyingSave}
            contentBySubMaster={contentBySubMaster}
            mediaOverrides={mediaOverrides}
            mediaSlots={mediaSlots}
            assetVersions={assetVersions}
            carouselSlideIndex={carouselSlideIndex}
            setCarouselSlideIndex={setCarouselSlideIndex}
            audioPlaying={audioPlaying}
            setAudioPlaying={setAudioPlaying}
            audioRefs={audioRefs}
            activeTrack={activeTrack}
            setActiveTrack={setActiveTrack}
            openDrawer={openDrawer}
            closeDrawer={closeDrawer}
            setPendingPin={setPendingPin}
            onSaveChanges={handleSaveChanges}
          />

          {/* Right Column - Form */}
          <AdBuilderForm
            adSpecOverview={adBuilderData?.ad_spec_overview ?? []}
            restrictedParameters={adBuilderData?.restricted_parameters ?? []}
            restrictedValues={restrictedValues}
            onRestrictedValueChange={(valueKey, value) =>
              setRestrictedValues((prev) => ({
                ...prev,
                [valueKey]: value,
              }))
            }
            contentItems={contentItems}
            objectiveItems={objectiveItems}
            contentValues={contentValues}
            setContentValues={setContentValues}
            linkAcrossVersions={linkAcrossVersions}
            setLinkAcrossVersions={setLinkAcrossVersions}
            urlErrors={urlErrors}
            setUrlErrors={setUrlErrors}
            mediaSlots={mediaSlots}
            mediaComponentType={adBuilderData?.media.media_component_type}
            isVideoMedia={isVideoMedia}
            mediaOverrides={mediaOverrides}
            activeVersionId={activeVersionId}
            assetVersions={assetVersions}
            onMediaSelect={handleMediaSelect}
            onMediaClear={handleMediaClear}
            lockedFieldNames={lockedFieldNames}
            lockedMediaComponentIds={lockedMediaComponentIds}
            mediaSlotDescriptions={mediaSlotDescriptions}
            changelog={changelog}
            isChangelogLoading={isChangelogLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default AdBuilder;
